import { useEffect } from "react";
import useSWR from "swr";
import { compassusNavigatorController } from "../../AppController";

const responseData = <T>(
  response:
    | { success: true; data: T }
    | { success: false; error: { message: string } }
): T => {
  if (response.success) return response.data;
  throw new Error(response.error.message);
};

export const useHhRpmNotifications = ({
  clinicId,
  enabled,
  listEnabled,
  page,
  pageSize = 20,
}: {
  clinicId: number;
  enabled: boolean;
  listEnabled: boolean;
  page: number;
  pageSize?: number;
}) => {
  const count = useSWR(
    enabled && clinicId > 0
      ? { key: "hh-rpm-communication-notification-count", clinicId }
      : null,
    async ({ clinicId: activeClinicId }) =>
      responseData(
        await compassusNavigatorController.getHhRpmCommunicationNotificationCount(
          { clinicId: activeClinicId }
        )
      ),
    {
      refreshInterval: 15_000,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      revalidateOnFocus: true,
    }
  );

  const episodes = useSWR(
    enabled && listEnabled && clinicId > 0
      ? {
          key: "hh-rpm-notification-episodes",
          clinicId,
          status: "all" as const,
          page,
          pageSize,
        }
      : null,
    async ({
      clinicId: activeClinicId,
      page: activePage,
      pageSize: activePageSize,
    }) =>
      responseData(
        await compassusNavigatorController.getHhRpmNotificationEpisodes({
          clinicId: activeClinicId,
          status: "all",
          limit: activePageSize,
          offset: (activePage - 1) * activePageSize,
        })
      ),
    {
      refreshInterval: 15_000,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      revalidateOnFocus: true,
    }
  );

  const mutateCount = count.mutate;
  useEffect(() => {
    if (!enabled || !listEnabled || !episodes.data) return;
    void mutateCount();
  }, [enabled, episodes.data, listEnabled, mutateCount]);

  return {
    count: count.data?.count ?? 0,
    episodes: episodes.data?.episodes ?? [],
    total: episodes.data?.total ?? 0,
    isLoading: episodes.isLoading,
    error: episodes.error,
    refresh: () => Promise.all([mutateCount(), episodes.mutate()]),
  };
};
